<template>
  <div class="announcement-preview">
    <v-card>
      <v-card-title>
        <div class="announcement-preview__title">{{createChapterStudioConstant.PREVIEW_TITLE}}</div>
      </v-card-title>
      <div class="pt-2 pb-2 announcement-preview__body">
        <div v-if="isGeneralAnnouncement || isWarningAnnouncement">
          <general-announcement-card
              :announcement="announcement"
              :is-full-view="generalAnnouncementReadMoreToggled"
              :action-button-text="generalAnnouncementActionButtonText"
              :on-action-button-click="toggleReadMoreState"
              :is-warning-announcement="isWarningAnnouncement"
              :on-close="() => {}"></general-announcement-card>
        </div>
        <div v-if="isImageAnnouncement" class="announcement-preview__image-announcement">
          <image-announcement-card :announcement="announcement"></image-announcement-card>
        </div>
      </div>
      <v-card-actions>
        <div class="preview-action-bar--wrapper">
          <div v-if="!canPublish" class="preview-action-bar__publish-disclaimer">{{createChapterStudioConstant.PUBLISH_REQUIRED_DISCLAIMER}}</div>
          <v-layout row justify-space-between class="preview-action-bar__main">
            <v-btn class="preview-action-bar__secondary-btn" @click="backAction">
              {{createChapterStudioConstant.ACTION.BACK_TO_EDIT}}
            </v-btn>
            <v-layout row class="preview-action-bar__save-button-stack">
              <v-btn :disabled="!canPublish" class="preview-action-bar__secondary-btn" @click="saveDraftAction">
                {{createChapterStudioConstant.ACTION.SAVE_DRAFT}}
              </v-btn>
              <v-btn class="preview-action-bar__primary-btn" :disabled="!canPublish" @click="publishAction">
                {{createChapterStudioConstant.ACTION.PUBLISH}}</v-btn>
            </v-layout>
          </v-layout>
        </div>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { createChapterStudioConstant } from "@/components/ChapterStudioPageManage/manageChapterStudio.constant";
import GeneralAnnouncementCard from "@/components/ChapterStudioPage/GeneralAnnouncementCard.vue";
import { chapterStudioPageConstant } from "@/components/ChapterStudioPage/chapterStudioPage.constant";
import ImageAnnouncementCarousel from "@/components/ChapterStudioPage/ImageAnnouncementCarousel.vue";
import ImageAnnouncementCard from "@/components/ChapterStudioPage/ImageAnnouncementCard.vue";

export default {
  name: 'PreviewAnnouncement',
  components: {
    ImageAnnouncementCard,
    ImageAnnouncementCarousel,
    GeneralAnnouncementCard,
  },
  data() {
    return {
      createChapterStudioConstant,
      chapterStudioPageConstant,
      generalAnnouncementReadMoreToggled: false,
    };
  },
  props: {
    announcement: {
      type: Object,
      required: true,
    },
    canPublish: {
      type: Boolean,
      required: true,
      default: false,
    },
    publishAction: {
      type: Function,
      required: true,
    },
    saveDraftAction: {
      type: Function,
      required: true,
    },
    backAction: {
      type: Function,
      required: true,
    },
  },
  computed: {
    isGeneralAnnouncement() {
      return this.announcement.recordTypeName === createChapterStudioConstant.RECORD_TYPE.GENERAL;
    },
    isWarningAnnouncement() {
      return this.announcement.recordTypeName === createChapterStudioConstant.RECORD_TYPE.WARNING;
    },
    isImageAnnouncement() {
      return this.announcement.recordTypeName === createChapterStudioConstant.RECORD_TYPE.IMAGE;
    },
    generalAnnouncementActionButtonText() {
      return this.generalAnnouncementReadMoreToggled
          ? chapterStudioPageConstant.CLOSE
          : chapterStudioPageConstant.READ_MORE;
    },
  },
  methods: {
    toggleReadMoreState() {
      this.generalAnnouncementReadMoreToggled = !this.generalAnnouncementReadMoreToggled;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";
@import "../../common/styles/_commonClasses.scss";

.announcement-preview {
  background-color: $white;
  border-radius: 8px;
  position: relative;

  .v-card {
    border-radius: 8px;
    box-shadow: none !important;
  }

  @include breakpointMin(sm) {
    width: 100%;
  }

  @include breakpointMin(md) {
    min-width: 800px;
  }

  &__title {
    padding: 0 5px;
    color: $ypo-blue;
    font-size: 24px;
    font-weight: 600;
  }

  &__body {
    padding: 0 20px;
    margin-bottom: 30px;
    max-height: 500px;
    overflow: auto;
    @extend %inner-scrollbar;

    @include breakpointMin(md) {
      max-height: none;
      min-height: 300px;
      margin-bottom: 70px;
    }
  }

  &__image-announcement {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .image-card {
      max-width: 361px;
    }
  }

  .preview-action-bar {
    &--wrapper {
      padding: 5px 20px 10px;
      width: 100%;
      background-color: $white;
    }

    &__publish-disclaimer {
      color: $ypo-error-red;
      font-size: 10px;
      font-weight: 500;

      @include breakpointMin(sm) {
        text-align: right;
      }
    }

    &__main {
      flex-direction: column-reverse;

      @include breakpointMin(sm) {
        flex-direction: row;
        justify-content: end;
        align-items: end;
      }
    }

    &__save-button-stack {
      display: flex;
      flex-direction: column-reverse;

      @include breakpointMin(sm) {
        flex-direction: row;
        justify-content: end;
      }
    }

    &__secondary-btn {
      background: $mild-grey !important;
      color: $white !important;
      border-radius: 8px;
      padding: 8px 28px;
      width: 100%;
      height: 32px;
      font-size: 14px;
      font-weight: 600;
      box-shadow: none !important;
      margin: 5px 0;

      @include breakpointMin(sm) {
        width: 190px;
        margin: 5px;
      }
    }

    &__primary-btn {
      background: $ypo-brand-blue !important;
      color: $white !important;
      border-radius: 8px;
      padding: 8px 28px;
      width: 100%;
      height: 32px;
      font-size: 14px;
      font-weight: 600;
      box-shadow: none !important;
      margin: 5px 0;

      @include breakpointMin(sm) {
        width: 190px;
        margin: 5px 0 0 10px;
      }
    }
  }
}
</style>

